/**
 * SplashMaker Events Frontend Handler
 *
 * Captures interactions with blocks that have splashmaker events assigned and
 * dispatches custom events for tracking and automation.
 */

import { camelCase, pascalCase } from 'change-case';
import * as z from 'zod/mini';
import { EventType } from './EventType';

/**
 * Event listener callback signature
 */
type SplashMakerEventListener = (eventType: string, element?: Element) => void;

const SplashMakerEventsList = z.array(z.string());

/**
 * SplashMaker Events API
 */
class SplashMakerEvents {
	private listeners: SplashMakerEventListener[] = [];

	/**
	 * Register a single event listener for all splashmaker events
	 */
	on(callback: SplashMakerEventListener): void {
		if (typeof callback === 'function') {
			this.listeners = [...this.listeners, callback];
		} else {
			console.warn('[SplashMaker] Callback must be a function');
		}
	}

	/**
	 * Trigger the listener for a specific event type
	 */
	trigger(eventType: string, element?: Element): void {
		this.listeners.forEach((listener, i) => {
			try {
				listener(eventType, element);
			} catch (error) {
				console.error(`[SplashMaker] Error in event listener ${i}:`, error);
			}
		});

		if (element) {
			// Dispatch native custom event for external integrations
			const customEvent = new CustomEvent(`splashmaker:${eventType}`, {
				detail: { element, eventType },
				bubbles: true,
				cancelable: true,
			});

			element.dispatchEvent(customEvent);
		}
	}

	/**
	 * Initialize event delegation at document level
	 * This works with dynamically added elements
	 */
	init(): void {
		// Use event delegation - attach single listener to document
		document.addEventListener('click', (e: MouseEvent) => {
			// Find the closest element with data-splashmaker-events
			const target = e.target as HTMLElement | null;

			const legacyEventClassPrefix = 'splash-report-click';
			const legacyEventClassPattern = /splash-report-click-(\w+)/;

			/**
			 * Support for legacy class-based event tagging
			 */
			const eventTypeClasses = Object.values(
				window.splashmakerEventsConfig.eventTypes,
			)
				.map((value: string) => camelCase(value))
				.map((value: string) => `${legacyEventClassPrefix}-${value}`);

			const element = target?.closest(
				`[data-splashmaker-events],${eventTypeClasses.map((value) => `.${value}`).join(',')}`,
			);

			if (!element) {
				return;
			}

			try {
				let events: string[] = [];

				// Parse splashmaker events JSON
				const encodedEvents = element.getAttribute('data-splashmaker-events');

				if (encodedEvents) {
					try {
						events = SplashMakerEventsList.parse(JSON.parse(encodedEvents));
					} catch (error) {
						const issues =
							error instanceof z.core.$ZodError ? error.issues : undefined;

						console.warn(
							'[SplashMaker] data-splashmaker-events attribute contained unexpected data',
							issues,
						);
					}
				}

				/**
				 * Support for legacy class-based event tagging (with deduplication via
				 * Set).
				 */
				events = Array.from(
					new Set([
						...events,
						...Array.from(element.classList)
							.filter((className) =>
								className.startsWith(legacyEventClassPrefix),
							)
							.map((className) => className.match(legacyEventClassPattern)?.[1])
							.filter((value) => typeof value === 'string')
							.map((value) => pascalCase(value)),
					]),
				);

				if (!Array.isArray(events) || events.length === 0) {
					return;
				}

				// Trigger each associated splashmaker event
				events.forEach((eventType) => {
					const parseResult = EventType.safeParse(eventType);

					if (parseResult.success) {
						this.trigger(parseResult.data, element);
					}
				});
			} catch (error) {
				console.error(
					'[SplashMaker] Error processing splashmaker events:',
					error,
					element,
				);
			}
		});
	}
}

// Initialize API
const instance = new SplashMakerEvents();
window.SplashMakerEvents = instance;

/**
 * Default event handler - log all events to console and send to Google Analytics
 */
instance.on((eventType) => {
	// Send event to Google Analytics if gtag is available and we have registration data
	if (typeof window.gtag === 'function') {
		const config = window.splashmakerEventsConfig;
		const registration = config?.registration;

		if (registration?.webletId) {
			try {
				window.gtag('event', eventType, {
					transport_type: 'beacon',
					weblet_id: registration.webletId,
				});
				console.debug(
					`Sent ${eventType} to Google Analytics with weblet_id: ${registration.webletId}`,
				);
			} catch (error) {
				console.error(
					'[SplashMaker] Error sending event to Google Analytics:',
					error,
				);
			}
		}
	} else {
		console.warn(
			'SplashMaker is not registered; events are not being tracked!',
		);
	}
});

// Initialize event delegation on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => instance.init());
} else {
	instance.init();
}
